import {useQuery} from "~/apollo/patches";
import {
    GetVesselCallDocument, GetVesselCallListQueryResult, GetVesselCallQueryResult,
    useFollowVesselMutation,
    useGetVesselCallListQuery,
    useGetVesselCallQuery,
    useUnfollowVesselMutation, VesselCallVessel
} from "~/queries";
import {useErrorHandler} from "~/components/ErrorHandling";
import {useCallback, useEffect, useRef} from "react";
import moment from "moment";
import {ApolloCache, makeReference, useApolloClient} from "@apollo/client";


export type useVesselCallListOptions = {
    query?: string
    status?: string[]
    dateFrom?: Date
    dateTo?: Date
    favorite?: boolean
}
export const useVesselCallList = (options: useVesselCallListOptions = {}) => {
    const handleError = useErrorHandler();
    const apollo = useApolloClient();
    const result = useGetVesselCallListQuery( {
        notifyOnNetworkStatusChange: true,
        variables: {
            page: null,
            query: options.query,
            status: options.status,
            dateFrom: options?.dateFrom ? moment(options.dateFrom).format("YYYY-MM-DD") : null,
            dateTo: options?.dateTo ? moment(options.dateTo).format("YYYY-MM-DD") : null,
            favorite: options.favorite,
        },
    });
    let {data, loading, error, refetch, fetchMore: _fetchMore} = result;
    const canFetchMore = Boolean(data?.vesselCalls?.pageInfo?.nextPage) && !loading;

    const fetchMore = useCallback(() => {
        if (!canFetchMore) {
            return;
        }
        _fetchMore({
            variables: {
                page: data.vesselCalls.pageInfo.nextPage
            },
        }).catch(handleError);
    }, [data?.vesselCalls?.pageInfo?.nextPage, canFetchMore, _fetchMore]);

    // remove previous cache
    useEffect(() => {
        if (!data && loading) {
            apollo.cache.evict({id: "ROOT_QUERY", fieldName: "vesselCalls"});
        }
    }, [data, loading, apollo])

    useEffect(() => {
        if (error) {
            handleError(error);
            const timeoutId = setTimeout(() => refetch(), 5000);
            return () => clearTimeout(timeoutId);
        }
    }, [error]);

    return {
        data: data?.vesselCalls?.vesselCalls,
        emptyFavorites: data?.vesselCalls?.emptyFavorites,
        loading: loading || error != null,
        canFetchMore,
        fetchMore
    }
}


export const useVesselCall = ({id}) => {
    const handleError = useErrorHandler();
    const apollo = useApolloClient();
    const {data, loading, error, refetch} = useGetVesselCallQuery({
        notifyOnNetworkStatusChange: true,
        variables: {
            id: id
        },
        fetchPolicy: "cache-and-network",
    });

    useEffect(() =>{
        if (error) {
            handleError(error);
        }
    }, [error]);

    return {
        loading: loading || error != null,
        data: data?.vesselCall
    }
}


export const useFollowVessel = (imo: string) => {
    const apollo = useApolloClient();
    const $removeFromList = useRef<boolean>();

    const updateFavorite = (cache: ApolloCache<any>, value: boolean) => {
        const ids = [];

        cache.modify({
            id: cache.identify(makeReference("ROOT_QUERY")),
            fields: {
                vesselCalls(existing, {readField}) {
                    if (existing) {
                        for (const item of existing?.vesselCalls) {
                            if (readField<VesselCallVessel>("vessel", item)?.imo == imo) {
                                ids.push(cache.identify(item));
                            }
                        }
                    }
                    return {...existing};
                },
                vesselCall(existing, {readField, toReference}) {
                    if (readField<VesselCallVessel>("vessel", existing)?.imo == imo) {
                        ids.push(cache.identify(existing));
                    }
                    return existing;
                },
            },
        });

        for (const id of ids) {
            cache.modify({
                id: id,
                fields: {
                    favorite(existing) {
                        return value;
                    }
                },
                broadcast: true,
                optimistic: false,
            })
        }

        $removeFromList.current = !value;
    }

    const [follow, followResult] = useFollowVesselMutation({
        notifyOnNetworkStatusChange: true,
        variables: {
            imo: imo
        },
        update(cache) {
            updateFavorite(cache, true);
        }
    });
    const [unfollow, unfollowResult] = useUnfollowVesselMutation({
        notifyOnNetworkStatusChange: true,
        variables: {
            imo: imo
        },
        update(cache) {
            updateFavorite(cache, false)
        }
    });


    // удаляем из списка избранных, если он не относится к избранным
    // useEffect(() => () => {
    //     if ($removeFromList.current) {
    //         apollo.cache.modify({
    //             id: apollo.cache.identify(makeReference("ROOT_QUERY")),
    //             fields: {
    //                 vesselCalls(existing, {readField, storeFieldName}) {
    //                     if (existing && storeFieldName.includes('"favorite":true')) { // check if favorite
    //                         for (const item of existing?.vesselCalls) {
    //                             if (readField<VesselCallVessel>("vessel", item)?.imo == imo) {
    //                                 apollo.cache.evict({id: apollo.cache.identify(item)})
    //                             }
    //                         }
    //                     }
    //                     return {...existing};
    //                 },
    //             }
    //         })
    //     }
    // }, [])

    const loading = followResult.loading || unfollowResult.loading;
    return {follow, unfollow, loading};
}